import { useEffect, useState } from "react";
import { Loader2, Upload, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const STATUS_OPTIONS = [
  ["", "Let the stamp decide"],
  ["approved", "Approved"],
  ["approved_as_noted", "Approved as Noted"],
  ["revise_resubmit", "Revise and Resubmit"],
  ["rejected", "Rejected"],
  ["for_record", "For Record"],
];

/** SUB-1 §2 — Log a return on a revision: upload the returned copy or pick an inbound email already
 *  in the app. The stamp sets the status only on an exact phrase match; otherwise it is proposed. */
export default function LogReturnDialog({ project, revision: preset, revisions = [], onClose, onDone }) {
  const [pickedId, setPickedId] = useState(preset?.id || "");
  const revision = preset || revisions.find((r) => r.id === pickedId) || null;
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [emails, setEmails] = useState(null);
  const [intakeId, setIntakeId] = useState("");
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 10));
  const [fromWho, setFromWho] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [comments, setComments] = useState("");
  const [stampText, setStampText] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (mode !== "email" || emails) return;
    opsApi.projectIntake(project.id)
      .then((d) => setEmails(d.items.filter((i) => i.doc_type === "submittal_return" || i.doc_type === "submittal")))
      .catch(() => setEmails([]));
  }, [mode, emails, project.id]);

  const submit = async () => {
    if (!revision) { toast.error("Pick the revision this return belongs to"); return; }
    if (mode === "upload" && !file) { toast.error("Choose the returned PDF"); return; }
    if (mode === "email" && !intakeId) { toast.error("Pick the inbound email"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      if (mode === "upload") form.append("file", file); else form.append("intake_id", intakeId);
      form.append("received_at", receivedAt ? `${receivedAt}T12:00:00+00:00` : "");
      form.append("from_who", fromWho);
      form.append("reviewer", reviewer);
      form.append("comments", comments);
      form.append("stamp_text", stampText);
      form.append("status", status);
      const res = await opsApi.submittalLogReturn(project.id, revision.id, form);
      if (res.auto) toast.success(`${revision.number} → ${res.package.status_label} (stamp “${res.phrase}”)`);
      else if (status) toast.success(`${revision.number} → ${res.package.status_label}`);
      else if (res.proposed) toast.info(`Return logged — proposed ${res.package.proposed_status_label}, confirm it on the revision`);
      else toast.info("Return logged — the stamp could not be read, set the status on the revision");
      onDone(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That return could not be logged");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-log-return-dialog">
        <h3 className="text-base font-semibold text-slate-900">Log a return{revision ? ` — ${revision.number}${revision.revision ? ` Rev ${revision.revision}` : ""}` : ""}</h3>
        <p className="mt-1 text-xs text-slate-500">The returned copy is filed to Documents / Submittals / Returned and attached to this revision. Ball in court flips to ABS.</p>
        {!preset && (
          <label className="mt-3 block text-xs font-medium text-slate-600">Revision
            <select value={pickedId} onChange={(e) => setPickedId(e.target.value)} data-testid="ops-log-return-revision"
                    className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
              <option value="">Pick the revision…</option>
              {revisions.filter((r) => !r.superseded).map((r) => (
                <option key={r.id} value={r.id}>{r.number}{r.revision ? ` Rev ${r.revision}` : ""} — {r.title}</option>
              ))}
            </select>
          </label>
        )}

        <div className="mt-3 flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
          {[["upload", "Upload", Upload], ["email", "From an inbound email", Mail]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`ops-log-return-mode-${k}`}
                    className={`flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 ${mode === k ? "bg-white font-medium text-slate-900 shadow-sm" : "text-slate-500"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {mode === "upload" ? (
          <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-xs text-slate-600 hover:border-slate-400">
            <Upload className="h-4 w-4" />
            {file ? file.name : "Choose the returned PDF"}
            <input type="file" accept="application/pdf" className="hidden" data-testid="ops-log-return-file"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        ) : (
          <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-slate-200 text-xs" data-testid="ops-log-return-emails">
            {!emails && <p className="px-3 py-2 text-slate-500">Loading…</p>}
            {emails && emails.length === 0 && <p className="px-3 py-2 text-slate-500">No submittal-return emails are in the app for this job.</p>}
            {(emails || []).map((i) => (
              <label key={i.id} className={`flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-slate-50 ${intakeId === i.id ? "bg-sky-50" : ""}`}>
                <input type="radio" name="intake" checked={intakeId === i.id} onChange={() => setIntakeId(i.id)} className="mt-0.5" />
                <span><span className="font-medium">{i.subject}</span>
                  {i.status && i.status !== "unmatched_return" && <span className="ml-1.5 rounded-full border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-500">{i.status === "linked" ? `linked to ${i.submittal_number || "a revision"}` : i.status === "pending_confirm" ? `pending on ${i.submittal_number || "a revision"}` : i.status}</span>}<br />
                  <span className="text-slate-500">{i.from_name || i.from_email} · {(i.created_at || "").slice(0, 10)}</span></span>
              </label>
            ))}
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block text-xs font-medium text-slate-600">Date received
            <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} className="mt-1 h-8 text-xs" data-testid="ops-log-return-date" />
          </label>
          <label className="block text-xs font-medium text-slate-600">From
            <Input value={fromWho} onChange={(e) => setFromWho(e.target.value)} placeholder="GC / architect" className="mt-1 h-8 text-xs" data-testid="ops-log-return-from" />
          </label>
          <label className="block text-xs font-medium text-slate-600">Reviewer
            <Input value={reviewer} onChange={(e) => setReviewer(e.target.value)} className="mt-1 h-8 text-xs" data-testid="ops-log-return-reviewer" />
          </label>
          <label className="block text-xs font-medium text-slate-600">Status
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ops-log-return-status"
                    className="mt-1 h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
        <label className="mt-3 block text-xs font-medium text-slate-600">Stamp text (optional — read from the PDF when left empty)
          <Input value={stampText} onChange={(e) => setStampText(e.target.value)} placeholder="e.g. Approved as Noted" className="mt-1 h-8 text-xs" data-testid="ops-log-return-stamp" />
        </label>
        <label className="mt-3 block text-xs font-medium text-slate-600">Reviewer comments
          <Textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3} className="mt-1 text-xs" data-testid="ops-log-return-comments" />
        </label>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy} data-testid="ops-log-return-cancel">Cancel</Button>
          <Button variant="data" size="sm" onClick={submit} disabled={busy} data-testid="ops-log-return-submit">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />} Log return
          </Button>
        </div>
      </div>
    </div>
  );
}
